// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Response-lifecycle stage model, ported from the Operator Console mock's
// `dec()` stage logic. A stage is derived from an alarm's ack / validate /
// work-order / owner state; StageChip.tsx + its sibling CSS module own the
// stage → color mapping so this stays pure derivation logic.

import { hasWorkOrderState, isClearedState } from '../types/alarmFsm';
import type { Alarm, AlarmStage } from '../types/alarm';

export interface StageResult {
  stage: AlarmStage;
  /** Chip label (e.g. "Work Order", "Closed · WO complete"). */
  label: string;
}

/**
 * True when the alarm has a work order.
 *
 * Before helios-aem #134, WO_CREATED/WO_COMPLETED were state-machine states, so
 * the alarm's own status was authoritative. After it the link is the alarm's
 * `workOrder` (or AEM's `workOrderId`), so both are read; callers resolve a link
 * this browser remembered into `workOrder` first (see StageChip). The ownerStatus regex
 * stays behind them only for UI-seeded alarms that carry no AEM status at all —
 * it was the sole signal before the state machine landed.
 */
export function hasWorkOrder(a: Alarm): boolean {
  return Boolean(
    hasWorkOrderState(a.status) ||
    a.workOrder ||
    a.workOrderId ||
    a.woRaised ||
    a.assignWo ||
    /wo\b|work order/i.test(a.ownerStatus ?? '')
  );
}

/** True when the alarm has been acknowledged. */
export function isAcked(a: Alarm): boolean {
  return Boolean(a.acked || a.assignAcked || a.ownerStatus === 'Acknowledged');
}

/**
 * Compute the stage + label for an alarm, mirroring the mock's `dec()` branch
 * order: closed → (un)validated → work order → in progress → default.
 */
export function alarmStage(a: Alarm): StageResult {
  const acked = isAcked(a);
  const woActive = hasWorkOrder(a);
  const validated = Boolean(a.validated);

  let stage: AlarmStage;
  let label: string;

  if (a.closed) {
    stage = 'closed';
    // A linked order alone says nothing about its progress: the single
    // lifecycle (helios-aem #134) lets an alarm clear or close while the order
    // is still open, so only the older signals may claim it is complete.
    const linkOnly =
      (a.workOrder || a.workOrderId) &&
      !hasWorkOrder({ ...a, workOrder: undefined, workOrderId: undefined });
    label =
      woActive && !linkOnly
        ? 'Closed · WO complete'
        : linkOnly
          ? 'Closed · WO linked'
          : /work order|WO/i.test(a.outcome ?? '')
            ? 'Closed · WO complete'
            : 'Closed · no WO';
  } else if (isClearedState(a.status)) {
    // The condition went away but nobody has closed the alarm: it stays in the
    // queue, and the chip says so rather than reading as new or in progress.
    stage = 'cleared';
    label = 'Cleared';
  } else if (!validated) {
    stage = acked ? 'unvalidated' : 'unacknowledged';
    label = acked ? 'Unvalidated' : 'Unacknowledged';
  } else if (woActive) {
    stage = 'work-order';
    label = 'Work Order';
  } else if (a.ownerStatus === 'In progress' || a.ownerStatus === 'Delegated') {
    stage = 'in-progress';
    label = 'In Progress';
  } else if (acked) {
    stage = 'in-progress';
    label = 'In Progress';
  } else {
    stage = 'unacknowledged';
    label = 'Unacknowledged';
  }

  return { stage, label };
}
